import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const TrustpilotReviews = () => {
  const { t } = useLanguage();
  const reviews = [
    {
      id: 1,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/jpp4pat5_image.png',
      rotation: -2,
      delay: 0.1
    },
    {
      id: 2,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/ca0p9ltr_image.png',
      rotation: 3,
      delay: 0.2
    },
    {
      id: 3,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/24m851ks_image.png',
      rotation: -1,
      delay: 0.3
    },
    {
      id: 4,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/7prybg24_image.png',
      rotation: 2,
      delay: 0.4
    },
    {
      id: 5,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/50965fgi_image.png',
      rotation: -3,
      delay: 0.5
    },
    {
      id: 6,
      image: 'https://customer-assets.emergentagent.com/job_compose-translate/artifacts/3rty23so_image.png',
      rotation: 1,
      delay: 0.6
    }
  ];

  return (
    <section id="reviews" className="py-16 bg-gradient-to-br from-purple-50 via-pink-50 to-white overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Header with Trustpilot Badge */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg mb-4">
            <Star className="w-5 h-5 fill-green-500 text-green-500" />
            <span className="font-bold text-gray-900">Trustpilot</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} className="w-4 h-4 fill-green-500 text-green-500" />
              ))}
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            {t('trustpilot.title')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-fuchsia-500">{t('trustpilot.titleHighlight')}</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('trustpilot.description')}
          </p>
        </div>

        {/* Reviews Grid - Scattered Cards */}
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {reviews.map((review) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 50, rotate: 0 }}
                whileInView={{ 
                  opacity: 1, 
                  y: 0, 
                  rotate: review.rotation,
                  transition: { 
                    duration: 0.6, 
                    delay: review.delay,
                    type: "spring",
                    stiffness: 100
                  }
                }}
                whileHover={{ 
                  scale: 1.05, 
                  rotate: 0,
                  zIndex: 10,
                  transition: { duration: 0.3 }
                }}
                viewport={{ once: true, margin: "-50px" }}
                className="cursor-pointer"
              >
                <div className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                  <div className="p-4">
                    <img 
                      src={review.image} 
                      alt={`Trustpilot Review ${review.id}`}
                      className="w-full h-auto rounded-xl"
                      loading="lazy"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Trustpilot CTA */}
        <div className="text-center mt-12">
          <a 
            href="https://www.trustpilot.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors text-sm font-medium"
          >
            <span>{t('trustpilot.seeAllReviews')}</span>
            <Star className="w-4 h-4 fill-green-500 text-green-500" />
            <span className="font-bold">Trustpilot</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default TrustpilotReviews;
